// Library Methods
import { useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";

// Components
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Alert from "src/components/shared/alerts/Alert/Alert";
import PelliRobsonGraph from "./PelliRobsonGraph/PelliRobsonGraph";
import TumblingEGraph from "./TumblingEGraph/TumblingEGraph";
import NonVisualFieldLegend from "../../../NonVisualFieldLegend/NonVisualFieldLegend";

// Contexts
import { ReportContext } from "src/contexts/reports/ReportContext";
import { ReportSectionContext } from "src/contexts/reports/ReportSectionContext";

// Constants
import { algorithmTypes, examCardEnums } from "src/constants/enums";

// Utils
import { isTerminatedExamReport } from "src/utils/exams/examReportsHelper";

const ContrastSensitivityGraph = () => {
  const { t } = useTranslation();

  const { exam } = useContext(ReportContext);
  const { examReport } = useContext(ReportSectionContext);

  const isTerminated = useMemo(
    () =>
      isTerminatedExamReport({
        exam,
        examReport,
        sectionName: examCardEnums.CONTRAST_SENSITIVITY.sectionName,
      }),
    [exam, examReport]
  );

  const examAlgorithm = useMemo(
    () => examReport?.exam?.algorithm,
    [examReport?.exam?.algorithm]
  );

  const isError = useMemo(() => {
    const isErrorPelliRobson =
      examAlgorithm === algorithmTypes.PELLI_ROBSON &&
      (!examReport?.state?.letters || !examReport?.state?.letters?.length);
    const isErrorTumblingE =
      examAlgorithm === algorithmTypes.TUMBLING_E &&
      (!examReport?.state?.directions ||
        !examReport?.state?.directions?.length);

    return isErrorPelliRobson || isErrorTumblingE;
  }, [
    examAlgorithm,
    examReport?.state?.directions,
    examReport?.state?.letters,
  ]);

  return (
    <Grid container gap="20px">
      {isTerminated && (
        <Alert
          variant="error"
          alertContent={t("Terminated Exam")}
          props={{ width: "100%" }}
        />
      )}

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          gap: "20px",
        }}
      >
        {!isError && <NonVisualFieldLegend />}

        <Box
          sx={{
            display: "flex",
            width: "100%",
            justifyContent: "center",
          }}
        >
          {examAlgorithm === algorithmTypes.PELLI_ROBSON ? (
            <PelliRobsonGraph examData={examReport} />
          ) : (
            <TumblingEGraph examData={examReport} />
          )}
        </Box>
      </Box>
    </Grid>
  );
};

export default ContrastSensitivityGraph;
